import { baseApi } from './baseApi';

export type DeploymentKind = 'client' | 'dotnet' | 'dotnet_runtime' | 'os_packages';
export type Outcome = 'succeeded' | 'failed';

export interface DeploymentCounts {
  total: number;
  succeeded: number;
  failed: number;
  in_flight: number;
}

export interface DeploymentSummary {
  id: string;
  kind: DeploymentKind;
  version: string;
  target_spec: Record<string, unknown>;
  options: Record<string, unknown>;
  created_by: string;
  created_at: string;
  counts: DeploymentCounts;
}

export interface DeploymentTarget {
  device_id: string;
  device_name: string | null;
  previous_version: string | null;
  command_id: string | null;
  /** Live status of the command ("pending", "executing", ...). */
  command_status: string | null;
  /** Null while the command is in flight. */
  outcome: Outcome | null;
  error: string | null;
  completed_at: string | null;
  /** What the Device reported with its Outcome; shape depends on the kind. */
  command_result: Record<string, unknown> | null;
}

export interface DeploymentDetail extends DeploymentSummary {
  targets: DeploymentTarget[];
}

export interface DeviceDeployment {
  deployment_id: string;
  kind: DeploymentKind;
  version: string;
  created_by: string;
  created_at: string;
  previous_version: string | null;
  command_status: string | null;
  outcome: Outcome | null;
  error: string | null;
  completed_at: string | null;
}

export interface CreateDeploymentRequest {
  kind: DeploymentKind;
  version: string;
  device_ids: string[];
  options?: { timeout?: number; rollback_on_failure?: boolean };
}

export const deploymentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createDeployment: builder.mutation<DeploymentDetail, CreateDeploymentRequest>({
      query: (body) => ({ url: '/deployments', method: 'POST', body }),
      invalidatesTags: [{ type: 'Deployment', id: 'LIST' }],
    }),

    getDeployments: builder.query<DeploymentSummary[], { kind?: DeploymentKind; limit?: number }>({
      query: ({ kind, limit = 10 }) => ({ url: '/deployments', params: { kind, limit } }),
      providesTags: [{ type: 'Deployment', id: 'LIST' }],
    }),

    getDeployment: builder.query<DeploymentDetail, string>({
      query: (id) => `/deployments/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Deployment', id }],
    }),

    getDeviceDeployments: builder.query<DeviceDeployment[], { deviceId: string; limit?: number }>({
      query: ({ deviceId, limit = 10 }) => ({
        url: `/deployments/devices/${deviceId}`,
        params: { limit },
      }),
      providesTags: [{ type: 'Deployment', id: 'LIST' }],
    }),
  }),
});

export const {
  useCreateDeploymentMutation,
  useGetDeploymentsQuery,
  useGetDeploymentQuery,
  useGetDeviceDeploymentsQuery,
} = deploymentsApi;
